/**
 * D-EZ-109 — ONE LINE OF THE WIRE, AND WHERE EVERY WORD IN IT CAME FROM.
 *
 * Every sentence the Wire prints is a template with slots. A slot is either a
 * NAME (a driver, a track, a sponsor, a race) or a NUMBER (a count, a place, a
 * points gap, a betting line), and the only names and numbers in the printed
 * text are the ones that came in through a slot. The line keeps its slots, so
 * `wire.grounding.test.ts` can strip them back out and prove nothing is left
 * over — no digit, no driver's name — that the facts did not supply.
 *
 * Which of several phrasings is used is a hash of stable ids plus how many
 * times this man has had this kind of line this season (`occurrence`), so a
 * driver who leads the points for six weeks reads six different sentences, and
 * no engine die is ever drawn.
 */

import { stableHash } from '../story/hometown.ts';

/** How a number is printed. 'money' takes Megabucks and prints dollars ("$4.2M"). */
export type NumFormat = 'int' | 'ord' | 'dec1' | 'odds' | 'word' | 'money';

/** Megabucks → "$4.2M" / "$350K" (1 Mb = $10,000, SAG/14). */
export function money(mb: number): string {
  const d = Math.round(mb * 10_000);
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}K`;
  return `$${d}`;
}

export interface NumSlot {
  n: number;
  f?: NumFormat;
  /** For 'odds': the printed line ("6-1"), already rounded to the ladder. */
  text?: string;
}

export type SlotValue = string | NumSlot;

export interface WireLine {
  /** What kind of line this is ('won', 'points-leader', 'odds-note'…), for the variety measure. */
  kind: string;
  /** Which phrasing of that kind was used (an index into its template list). */
  variant: number;
  /** The template, before filling — the variety measure counts these. */
  template: string;
  text: string;
  /** Every name that went in, by slot. */
  names: Record<string, string>;
  /** Every number that went in, by slot, raw. */
  nums: Record<string, number>;
  /** The drivers this line is about, for the grounding test. */
  driverIds: string[];
}

export function ordinal(n: number): string {
  const v = Math.abs(n) % 100;
  const s = v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${s}`;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];

/** Small counts read better as words in a sentence; the grounding test knows both forms. */
export function numberWord(n: number): string {
  return Number.isInteger(n) && n >= 0 && n < WORDS.length ? WORDS[n] : String(n);
}

export function formatNum(slot: NumSlot): string {
  switch (slot.f) {
    case 'ord':
      return ordinal(slot.n);
    case 'dec1':
      return slot.n.toFixed(1);
    case 'odds':
      return slot.text ?? String(slot.n);
    case 'word':
      return numberWord(slot.n);
    case 'money':
      return money(slot.n);
    default:
      return String(slot.n);
  }
}

const SLOT = /\{(\w+)\}/g;

/** True when every `{slot}` in the template has a value. */
export function canSay(template: string, slots: Readonly<Record<string, SlotValue | null | undefined>>): boolean {
  for (const m of template.matchAll(SLOT)) {
    const v = slots[m[1]];
    if (v === null || v === undefined || v === '') return false;
  }
  return true;
}

/**
 * Fill one template. Plurals are written in the template as `{n|race|races}`
 * — the count's slot, then the singular and plural word — so "1 races" can
 * never print.
 */
export function fillTemplate(template: string, slots: Readonly<Record<string, SlotValue | null | undefined>>): string {
  const withPlurals = template.replace(/\{(\w+)\|([^|}]*)\|([^}]*)\}/g, (_, k: string, one: string, many: string) => {
    const v = slots[k];
    const n = typeof v === 'object' && v ? v.n : Number(v);
    return n === 1 ? one : many;
  });
  const out = withPlurals.replace(SLOT, (_, k: string) => {
    const v = slots[k];
    if (v === null || v === undefined) return '';
    return typeof v === 'string' ? v : formatNum(v);
  });
  const fixed = articles(out);
  return fixed.charAt(0).toUpperCase() + fixed.slice(1);
}

/**
 * "a 8th" → "an 8th", "a associate" → "an associate". By sound: an 8, 11, 18,
 * 80-something; a vowel letter, except the ones that sound like "you" or "won".
 */
export function articles(text: string): string {
  return text.replace(/\b([Aa]) (?=(\S+))/g, (whole, a: string, next: string) => {
    const an =
      /^(8|11|18)(\D|$)/.test(next) ||
      /^8\d(\D|$)/.test(next) ||
      (/^[aeiou]/i.test(next) && !/^(one|once|uni|use|usu|euro|ewe)/i.test(next));
    return an ? `${a}n ` : whole;
  });
}

/**
 * Pick a phrasing and fill it. `key` names the moment (league, season, race,
 * driver); `occurrence` rotates through the phrasings when the same man gets
 * the same kind of line again. Templates whose slots cannot be filled are
 * skipped; returns null when none can be.
 */
export function say(
  kind: string,
  templates: readonly string[],
  slots: Readonly<Record<string, SlotValue | null | undefined>>,
  opts: { key: string; occurrence?: number; driverIds?: readonly string[] },
): WireLine | null {
  const usable = templates.map((t, i) => [t, i] as const).filter(([t]) => canSay(t, slots));
  if (usable.length === 0) return null;
  const base = stableHash(`wire|${kind}|${opts.key}`);
  const pick = usable[(base + (opts.occurrence ?? 0)) % usable.length];
  const [template, variant] = pick;
  const names: Record<string, string> = {};
  const nums: Record<string, number> = {};
  for (const m of template.matchAll(SLOT)) {
    const v = slots[m[1]];
    if (typeof v === 'string') names[m[1]] = v;
    else if (v) nums[m[1]] = v.n;
  }
  for (const m of template.matchAll(/\{(\w+)\|[^|}]*\|[^}]*\}/g)) {
    const v = slots[m[1]];
    if (v && typeof v === 'object') nums[m[1]] = v.n;
  }
  return {
    kind,
    variant,
    template,
    text: fillTemplate(template, slots),
    names,
    nums,
    driverIds: [...(opts.driverIds ?? [])],
  };
}

export const num = (n: number, f: NumFormat = 'int'): NumSlot => ({ n, f });
